import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useBooking } from '../../context/BookingContext';
import { useGeolocation } from '../../hooks/useGeolocation';
import { useRoute } from '../../hooks/useRoute';
import { Button } from '../ui/button';
import { Card } from '../ui/card';
import { Avatar, AvatarFallback, AvatarImage } from '../ui/avatar';
import { Menu, X, CreditCard, History, LogOut, Wallet, ArrowLeft, UserCircle, Gift, Info, Phone } from 'lucide-react';
import BookingForm from './BookingForm';
import RouteMap from './RouteMap';
import ActiveBooking from './ActiveBooking';
import SupportChat from './SupportChat';
import { BrandLogo } from '../BrandLogo';
import { photoSrc } from '../../lib/photos';

const StatChip = ({ icon: Icon, label, value, tone, testId }) => (
  <Card className="p-3 flex items-center gap-3" data-testid={testId}>
    <div className={`w-10 h-10 rounded-full flex items-center justify-center ${tone}`}><Icon className="w-5 h-5" /></div>
    <div>
      <p className="text-xs text-muted-foreground">{label}</p>
      <p className="text-lg font-bold leading-tight">{value}</p>
    </div>
  </Card>
);

const UserDashboard = () => {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const { activeBooking } = useBooking();
  const { coords } = useGeolocation();
  const [showMenu, setShowMenu] = useState(false);
  const [pickup, setPickup] = useState(null);
  const [drop, setDrop] = useState(null);
  const { route, error: routeError } = useRoute(pickup, drop);

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  if (activeBooking && !['completed', 'cancelled'].includes(activeBooking.status)) {
    return <ActiveBooking />;
  }

  return (
    <div className="min-h-screen bg-background">
      <header className="bg-white border-b sticky top-0 z-40">
        <div className="container mx-auto px-4 py-4 flex justify-between items-center">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="icon" onClick={() => navigate('/')} data-testid="dashboard-home-btn">
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <BrandLogo variant="compact" className="h-8 w-auto" />
          </div>
          <div className="flex items-center gap-4">
            <div className="hidden md:flex items-center gap-2 bg-muted px-4 py-2 rounded-lg">
              <Wallet className="w-5 h-5 text-muted-foreground" />
              <span className="font-semibold">₹{user?.wallet || 0}</span>
            </div>
            <Button variant="ghost" size="icon" onClick={() => setShowMenu(!showMenu)} data-testid="dashboard-menu-btn">
              {showMenu ? <X /> : <Menu />}
            </Button>
          </div>
        </div>
      </header>

      {showMenu && (
        <div className="fixed inset-0 bg-black/50 z-50" onClick={() => setShowMenu(false)}>
          <div className="absolute right-0 top-0 h-full w-80 bg-white shadow-2xl p-6" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center gap-3 pb-6 border-b">
              <Avatar className="w-16 h-16">
                <AvatarImage src={photoSrc(user?.photoUrl)} />
                <AvatarFallback>{user?.name?.[0] || 'U'}</AvatarFallback>
              </Avatar>
              <div>
                <p className="font-semibold text-lg">{user?.name}</p>
                <p className="text-sm text-muted-foreground">{user?.phone}</p>
              </div>
            </div>
            <div className="space-y-2 mt-6">
              <Button variant="ghost" className="w-full justify-start" data-testid="menu-profile-btn"
                onClick={() => { setShowMenu(false); navigate('/user/profile'); }}>
                <UserCircle className="w-5 h-5 mr-3" />My Profile
              </Button>
              <Button variant="ghost" className="w-full justify-start" data-testid="menu-referral-btn"
                onClick={() => { setShowMenu(false); navigate('/user/referral'); }}>
                <Gift className="w-5 h-5 mr-3" />Refer &amp; Earn
              </Button>
              <Button variant="ghost" className="w-full justify-start" data-testid="menu-history-btn"
                onClick={() => { setShowMenu(false); navigate('/user/history'); }}>
                <History className="w-5 h-5 mr-3" />Booking History
              </Button>
              <Button variant="ghost" className="w-full justify-start" data-testid="menu-fastag-btn"
                onClick={() => { setShowMenu(false); navigate('/user/fastag'); }}>
                <CreditCard className="w-5 h-5 mr-3" />FASTag Recharge
              </Button>
              <Button variant="ghost" className="w-full justify-start" data-testid="menu-about-btn"
                onClick={() => { setShowMenu(false); navigate('/about'); }}>
                <Info className="w-5 h-5 mr-3" />About Us
              </Button>
              <Button variant="ghost" className="w-full justify-start" data-testid="menu-contact-btn"
                onClick={() => { setShowMenu(false); navigate('/contact'); }}>
                <Phone className="w-5 h-5 mr-3" />Contact Us
              </Button>
              <Button variant="ghost" className="w-full justify-start text-red-600 hover:text-red-700 hover:bg-red-50"
                onClick={handleLogout} data-testid="menu-logout-btn">
                <LogOut className="w-5 h-5 mr-3" />Logout
              </Button>
            </div>
          </div>
        </div>
      )}

      <main className="container mx-auto px-4 py-6">
        <div className="grid grid-cols-3 gap-3 mb-6">
          <StatChip icon={Wallet} label="Wallet" value={`₹${user?.wallet || 0}`} tone="bg-accent/15 text-primary" testId="stat-wallet" />
          <StatChip icon={CreditCard} label="FASTag" value={`₹${user?.fastagBalance || 0}`} tone="bg-green-100 text-green-600" testId="stat-fastag" />
          <StatChip icon={History} label="Total Trips" value={user?.totalTrips ?? 0} tone="bg-accent/15 text-accent-dark" testId="stat-trips" />
        </div>

        <div className="grid lg:grid-cols-5 gap-6 items-start">
          <div className="lg:col-span-2">
            <BookingForm pickup={pickup} drop={drop} onPickupChange={setPickup} onDropChange={setDrop}
              route={route} userCoords={coords} />
          </div>
          <div className="lg:col-span-3 lg:sticky lg:top-24">
            <Card className="overflow-hidden">
              <RouteMap pickup={pickup} drop={drop} route={route} fallbackCenter={coords} height={560} />
              {routeError && (
                <p className="px-4 py-2 text-xs text-amber-700 bg-amber-50" data-testid="route-error">{routeError}</p>
              )}
            </Card>
          </div>
        </div>
      </main>

      <SupportChat />
    </div>
  );
};

export default UserDashboard;
